import axios from 'axios';
import GlobalConfig, { setToken, setUser, setCurrentShift, clearAuth } from '../config/global';
import { applyEncryptionInterceptor } from '../config/encryption';

const api = axios.create({
  baseURL: GlobalConfig.API_BASE_URL,
});

applyEncryptionInterceptor(api);

// Add token to requests
api.interceptors.request.use(
  (config) => {
    if (GlobalConfig.token) {
      config.headers.Authorization = `Bearer ${GlobalConfig.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('api/cashier/login', {
      username,
      password
    });
    
    if (response.data.data) {
      setToken(response.data.data.access_token);
      setUser(response.data.data.employer);
      setCurrentShift(response.data.data.current_shift);
    }
    
    return response.data;
  },

  logout: async () => {
    try {
      const response = await api.post('api/cashier/logout');
      clearAuth();
      return response.data;
    } catch (error) {
      console.error('Logout error:', error);
      clearAuth();
      throw error;
    }
  },

  changePassword: async (passwordData) => {
    const response = await api.post('api/cashier/change-password', passwordData);
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('api/cashier/profile');
    return response.data;
  }
};

export const shiftAPI = {
  clockIn: async (notes = '') => {
    const response = await api.post('api/cashier/clock-in', { notes });
    return response.data;
  },

  startBreak: async (notes = '') => {
    const response = await api.post('api/cashier/start-break', { notes });
    return response.data;
  },

  endBreak: async () => {
    const response = await api.post('api/cashier/end-break');
    return response.data;
  },

  clockOut: async (notes = '') => {
    const response = await api.post('api/cashier/clock-out', { notes });
    return response.data;
  },

  getTodayShifts: async () => {
    const response = await api.get('api/cashier/today-shifts');
    return response.data;
  }
};

export const posAPI = {
  // Product APIs
  getProducts: () => api.get('api/cashier/get-all-products'),
  searchProducts: (query) => api.get(`api/products?search=${query}`),
  
  // Barcode API
  scanBarcode: (barcode) => api.get(`api/barcodes/${barcode}`),
  
  // Order APIs
  createOrder: (orderData) => api.post('api/cashier/orders', orderData),
  getOrder: (orderId) => api.get(`api/cashier/orders/${orderId}`),
  updateOrder: (orderId, orderData) => api.put(`api/cashier/orders/${orderId}`, orderData),
  
  // Payment APIs
  processPayment: (paymentData) => api.post('api/cashier/payments', paymentData)
};

// Add ecommerce API methods
export const ecommerceAPI = {
  // Get orders by status
  getOrdersByStatus: (status) => api.get(`api/ecommerce/orders/status/${status}`),
  
  // Update order status
  updateOrderStatus: (orderId, statusData) => api.put(`api/ecommerce/orders/${orderId}/status`, statusData),
  
  // Get order details
  getOrderDetails: (orderId) => api.get(`api/ecommerce/orders/${orderId}`),
};

export default api;